import React, { useEffect, useState } from "react";
import {
  Form,
  Input,
  Button,
  message,
  Spin,
  Card,
  Typography,
  Space,
  Row,
  Col,
  Avatar,
  Divider,
} from "antd";
import axios from "axios";
import SummaryApi from "../../../common/index";
import {
  EditOutlined,
  SaveOutlined,
  CloseOutlined,
  UserOutlined,
  ShopOutlined,
} from "@ant-design/icons";
import "../../../Styles/SellerStylings/SellerProfile.css";

const BASE_URL = process.env.REACT_APP_BACKEND_URL;
const { Title, Text } = Typography;

function SellerProfile({ token }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [initialValues, setInitialValues] = useState({});
  const [profilePic, setProfilePic] = useState(null);

 const fetchProfile = async () => {
  try {
    setLoading(true);
    const res = await axios.get(SummaryApi.sellerProfile.url, {
      headers: { Authorization: `Bearer ${token}` },
    });

    const seller = res.data.seller;

    // Set form values
    const values = {
      name: seller.name,
      phone: seller.phone,
      storeName: seller.store_name,
      storeStreet: seller.store_street,
      storeArea: seller.store_area,
      storeCity: seller.store_city,
      storePincode: seller.store_pincode,
      district: seller.district,
      state: seller.state,
    };

    form.setFieldsValue(values);
    setInitialValues(values);

    // Set profile picture URL
    setProfilePic(seller.profile_pic); // <- Add this

  } catch (err) {
    console.error("Error fetching seller profile:", err);
    message.error("Failed to load profile ❌");
  } finally {
    setLoading(false);
  }
};

const handleSellerProfilePicUpload = async (e) => {
  const file = e.target.files[0];
  if (!file) return;

  const formData = new FormData();
  formData.append("ProfilePic", file);

  try {
    await axios.put(
      SummaryApi.uploadSellerProfilePic.url,
      formData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      }
    );

    message.success("Profile picture updated");
    fetchProfile(); // refresh profile data
  } catch (err) {
    console.error(err);
    message.error("Failed to upload profile picture");
  }
};

  useEffect(() => {
    fetchProfile();
  }, []);

  const handleFinish = async (values) => {
    try {
      await axios.put(SummaryApi.updateProfile.url, values, {
        headers: { Authorization: `Bearer ${token}` },
      });
      message.success("Profile updated successfully");
      setIsEditing(false);
      fetchProfile();
    } catch (err) {
      console.error("Error updating profile:", err);
      message.error("Failed to update profile ❌");
    }
  };

  const handleCancel = () => {
    form.setFieldsValue(initialValues);
    setIsEditing(false);
  };

  if (loading)
    return (
      <div className="profile-loading">
        <Spin size="large" tip="Loading Profile..." />
      </div>
    );

  return (
    <div className="seller-profile-container">
      <Card bordered={false} className="seller-profile-card">
        <div className="seller-profile-header">
          <div className="seller-header-left">
          <div className="seller-avatar-section">

  {/* Hidden Input */}
  <input
    type="file"
    id="sellerProfilePicInput"
    accept="image/*"
    style={{ display: "none" }}
    onChange={handleSellerProfilePicUpload}
    disabled={!isEditing}
  />

  {/* Avatar Wrapper */}
  <div
    className={`seller-avatar-wrapper ${isEditing ? "avatar-editing" : ""}`}
    onClick={() => {
      if (isEditing) document.getElementById("sellerProfilePicInput").click();
    }}
    style={{ cursor: isEditing ? "pointer" : "default" }}
  >
    <Avatar
      size={80}
      src={profilePic ? `${BASE_URL}${profilePic}` : null}
      icon={!profilePic ? <UserOutlined /> : null}
    />
    {isEditing && (
      <Text type="secondary" style={{ marginTop: 6 }}>
        Click to upload
      </Text>
    )}
  </div>

</div>

            <div>
              <Title level={4} className="seller-name">
                {form.getFieldValue("name") || "Seller Name"}
              </Title>
              <Text type="secondary">
                {form.getFieldValue("storeName") || "Store Name"}
              </Text>
            </div>
          </div>

          {!isEditing ? (
            <Button
              style={{background:"#1129ce", color:"white"}}
              icon={<EditOutlined />}
              onClick={() => setIsEditing(true)}
              className="edit-btn"
            >
              Edit Profile
            </Button>
          ) : (
            <Space>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                htmlType="submit"
                form="profileForm"
                className="save-btn"
              >
                Save
              </Button>
              <Button
                icon={<CloseOutlined />}
                onClick={handleCancel}
                danger
                className="cancel-btn"
              >
                Cancel
              </Button>
            </Space>
          )}
        </div>

        <Divider />

        <Form
          id="profileForm"
          layout="vertical"
          form={form}
          onFinish={handleFinish}
          disabled={!isEditing}
        >
          <Row gutter={24}>
            {/* Left Column: Personal Info */}
            <Col xs={24} md={12}>
              <Title level={5}>
                <UserOutlined /> Personal Information
              </Title>

              <Form.Item
                name="name"
                label="Full Name"
                rules={[{ required: true, message: "Please enter your name" }]}
              >
                <Input placeholder="Enter your full name" />
              </Form.Item>

              <Form.Item
                name="phone"
                label="Phone Number"
                rules={[{ required: true, message: "Enter valid phone number" }]}
              >
                <Input
                  placeholder="Enter phone number"
                  maxLength={10}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, "");
                    if (value.length <= 10) {
                      form.setFieldsValue({ phone: value });
                    }
                  }}
                />
              </Form.Item>
               <Form.Item
                name="district"
                label="District"
                rules={[{ required: true }]}
              >
                <Input placeholder="Enter district" />
              </Form.Item>

              <Form.Item
                name="state"
                label="State"
                rules={[{ required: true }]}
              >
                <Input placeholder="Enter state" />
              </Form.Item>
            </Col>

            {/* Right Column: Store Info */}
            <Col xs={24} md={12}>
              <Title level={5}>
                <ShopOutlined /> Store Information
              </Title>

              <Form.Item
                name="storeName"
                label="Store Name"
                rules={[{ required: true }]}
              >
                <Input placeholder="Enter store name" />
              </Form.Item>

              <Form.Item
                name="storeStreet"
                label="Street"
                rules={[{ required: true }]}
              >
                <Input placeholder="Enter street address" />
              </Form.Item>

              <Form.Item name="storeArea" label="Area" rules={[{ required: true }]}>
                <Input placeholder="Enter area/locality" />
              </Form.Item>

              <Form.Item name="storeCity" label="City" rules={[{ required: true }]}>
                <Input placeholder="Enter city" />
              </Form.Item>

              <Form.Item
                name="storePincode"
                label="Pincode"
                rules={[{ required: true }]}
              >
                <Input placeholder="Enter pincode" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Card>
    </div>
  );
}

export default SellerProfile;
